import React, { useEffect, useRef, useState } from 'react';

import { Row, Col, Input, Select, Switch } from 'antd';

import { constants } from 'helpers';
import type IProduct from 'interfaces/IProduct';
import { run } from 'validations/ruleRunner';

import Validations from './validations';
import { FormContent } from '../FormContent';

enum PARAMETERS {
  POST = 1,
  DEFAULT = 2,
  NONE = 3,
  GET = 99
}

interface IProps {
  loading: boolean;
  fade?: boolean;
  product?: IProduct;
  handleImageChange?: (...args: any[]) => any;
  handleUploadEvents?: (...args: any[]) => any;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  categoryList?: any;
  onChangeRequiredFields?: (...args: any[]) => any;
}

function Advanced(props: IProps) {
  const { loading, product, handleChange, onChangeRequiredFields } = props;

  const [validationErrors, setValidationErrors] = useState<any>(() => run(props.product, Validations));
  const prevProductRef = useRef(props.product);

  const errorFor = (field: any) => {
    return validationErrors[field] || '';
  };

  const isValid = (field: any) => {
    if (validationErrors[field]) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!loading && product !== prevProductRef.current) {
      const newValidationErrors = run(product, Validations);
      setValidationErrors(newValidationErrors);

      if (onChangeRequiredFields) {
        onChangeRequiredFields(
          'advancedRequiredFields',
          newValidationErrors ? Object.keys(newValidationErrors).length : 0
        );
      }
    }

    prevProductRef.current = product;
  }, [loading, product, onChangeRequiredFields]);

  const isUrlAppBlinket =
    product?.url_thankyou_card?.includes('app.blinket') || product?.url_thankyou_boleto?.includes('app.blinket');

  return (
    <FormContent title='Avançado' loading={loading}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <div className='relative'>
            <label className='mb-1 block text-sm'>Url obrigado para cartão e PIX</label>
            <Input
              id='url_thankyou_card'
              className='w-full'
              status={isValid('url_thankyou_card') ? 'error' : undefined}
              value={product.url_thankyou_card}
              onChange={e => handleChange('url_thankyou_card', e.target.value)}
            />
            <p className='mt-1! mb-0! text-xs text-gray-500'>
              {isValid('url_thankyou_card') ? errorFor('url_thankyou_card') : 'Ex: https://obrigadocartao.com.br'}
            </p>
          </div>
        </Col>
        <Col span={24}>
          <div className='relative'>
            <label className='mb-1 block text-sm'>Url obrigado para boleto</label>
            <Input
              id='url_thankyou_boleto'
              className='w-full'
              status={isValid('url_thankyou_boleto') ? 'error' : undefined}
              value={product.url_thankyou_boleto}
              onChange={e => handleChange('url_thankyou_boleto', e.target.value)}
            />
            <p className='mt-1! mb-0! text-xs text-gray-500'>
              {isValid('url_thankyou_boleto') ? errorFor('url_thankyou_boleto') : 'Ex: https://obrigadoboleto.com.br'}
            </p>
          </div>
        </Col>
        <Col span={24}>
          <label className='mb-1 block text-sm'>Parâmetro</label>
          <Select
            id='format'
            className='w-full'
            value={product.thankyou_type}
            onChange={value => handleChange('thankyou_type', value)}
            options={[
              { value: PARAMETERS.NONE, label: 'Nenhum' },
              { value: PARAMETERS.DEFAULT, label: 'Padrão' },
              ...(!isUrlAppBlinket ? [{ value: PARAMETERS.POST, label: 'Post' }] : []),
              { value: PARAMETERS.GET, label: 'Get' }
            ]}
          />
        </Col>

        <Col span={24}>
          <div className='relative'>
            <label className='mb-1 block text-sm'>Url para o produto inativo</label>
            <Input
              id='alternative_url'
              className='w-full'
              status={isValid('alternative_url') ? 'error' : undefined}
              value={product.alternative_url}
              onChange={e => handleChange('alternative_url', e.target.value)}
            />
            <p className='mt-1 text-xs text-gray-500'>
              {isValid('alternative_url') ? errorFor('alternative_url') : 'Ex: http://site.com'}
            </p>
          </div>
        </Col>
        <Col span={24}>
          <div className='flex items-center gap-2'>
            <Switch checked={product.email_welcome} onChange={checked => handleChange('email_welcome', checked)} />
            <span>Email de acesso da Eduzz</span>
          </div>
          <p className='mt-1! mb-0! text-xs text-gray-500'>
            Envia e-mail de boas vindas de novo usuário com login e senha da Eduzz
          </p>
        </Col>
        <Col span={24}>
          <div className='flex items-center gap-2'>
            <Switch
              disabled={product.ask_address_thanks}
              checked={product.ask_address_checkout}
              onChange={checked => handleChange('ask_address_checkout', checked)}
            />
            <span>Endereço no checkout</span>
          </div>
          <p className='mt-1! mb-0! text-xs text-gray-500'>
            Deseja que seja pedido o endereço do cliente na página de checkout
          </p>
        </Col>
        {product.type !== constants.PHYSICAL && (
          <Col span={24}>
            <div className='flex items-center gap-2'>
              <Switch
                disabled={product.ask_address_checkout}
                checked={product.ask_address_thanks}
                onChange={checked => handleChange('ask_address_thanks', checked)}
              />
              <span>Endereço pós compra</span>
            </div>
            <p className='mt-1! mb-0! text-xs text-gray-500'>
              Deseja que seja pedido o endereço do cliente após confirmação da compra
            </p>
          </Col>
        )}

        <Col span={24}>
          <div className='flex items-center gap-2'>
            <Switch
              checked={product.document_on_checkout}
              onChange={checked => handleChange('document_on_checkout', checked)}
            />
            <span>CPF/CNPJ do pagador</span>
          </div>
          <p className='mt-1! mb-0! text-xs text-gray-500'>
            Será solicitado o CPF/CNPJ do titular do cartão no checkout
          </p>
        </Col>
        {(product.type === constants.PHYSICAL || product.type === constants.TICKET) && (
          <Col span={24}>
            <div className='flex items-center gap-2'>
              <Switch checked={product.hide_quantity} onChange={checked => handleChange('hide_quantity', checked)} />
              <span>Ocultar quantidade</span>
            </div>
            <p className='mt-1! mb-0! text-xs text-gray-500'>
              Oculta o campo de quantidade do produto no checkout deixando o valor padrão como 1 unidade
            </p>
          </Col>
        )}
      </Row>
    </FormContent>
  );
}

export default Advanced;
